import sanitizeHtml from "sanitize-html";

const PADDING_VALUE = String.raw`(?:(?:\d+(?:\.\d+)?|\.\d+)(?:px|em|rem|%|pt|in|cm)|0)`;

const MARGIN_VALUE = String.raw`(?:${PADDING_VALUE}|auto)`;

const oneToFourSpacingValues = (value: string): RegExp[] => [
    new RegExp(`^${value}(?:\\s+${value}){0,3}$`, "i"),
];

const PARAGRAPH_MARGIN_VALUES = oneToFourSpacingValues(MARGIN_VALUE);

const PARAGRAPH_PADDING_VALUES = oneToFourSpacingValues(PADDING_VALUE);

const PARAGRAPH_SPACING_STYLES: Record<string, RegExp[]> = {
    margin: PARAGRAPH_MARGIN_VALUES,
    "margin-top": PARAGRAPH_MARGIN_VALUES,
    "margin-bottom": PARAGRAPH_MARGIN_VALUES,
    "margin-left": PARAGRAPH_MARGIN_VALUES,
    "margin-right": PARAGRAPH_MARGIN_VALUES,
    padding: PARAGRAPH_PADDING_VALUES,
    "padding-top": PARAGRAPH_PADDING_VALUES,
    "padding-bottom": PARAGRAPH_PADDING_VALUES,
    "padding-left": PARAGRAPH_PADDING_VALUES,
    "padding-right": PARAGRAPH_PADDING_VALUES,
};

interface SanitizedMarkUp {
    __html: string;
}

export const getSanitizedMarkUpFromEmailBody = (htmlBody: string): SanitizedMarkUp => {
    const parser = new DOMParser();
    const parsedHtml = parser.parseFromString(htmlBody, "text/html");
    const dirtyHtml = parsedHtml.body.innerHTML;

    const colorCodeRegexMatchers = {
        hex: /^#(0x)?[0-9a-f]+$/i,
        rgb: /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/,
        name: /^(blue|violet|orange|gray)$/,
    };

    const cleanHtml = sanitizeHtml(dirtyHtml, {
        allowedTags: [
            "br",
            "u",
            "b",
            "i",
            "em",
            "strong",
            "a",
            "img",
            "p",
            "span",
            "div",
            "h1",
            "h2",
            "ul",
            "ol",
            "li",
        ],
        allowedAttributes: {
            a: ["href", "target"],
            img: ["src", "alt", "width", "height"],
            p: ["style"],
            span: ["style"],
            div: ["style"],
        },
        allowedSchemesByTag: {
            img: ["https"],
        },
        allowedStyles: {
            "*": {
                // Match HEX and RGB and ColorNames
                color: [
                    colorCodeRegexMatchers.hex,
                    colorCodeRegexMatchers.rgb,
                    colorCodeRegexMatchers.name,
                ],
                "background-color": [
                    colorCodeRegexMatchers.hex,
                    colorCodeRegexMatchers.rgb,
                    colorCodeRegexMatchers.name,
                ],
                "text-align": [/^left$/, /^right$/, /^center$/],
                "font-family": [/^[a-z0-9\s,"'-]+$/i],
                "font-size": [/^\d+(?:\.\d+)?(?:px|em|%|pt|rem)$/],
                "margin-left": [/^\d+(?:\.\d+)?(?:px|em|%|rem)$/],
            },
            p: PARAGRAPH_SPACING_STYLES,
        },
    });

    return { __html: cleanHtml };
};

const RECEIVED_EMAIL_COLOR_VALUES = [
    /^#(0x)?[0-9a-f]+$/i,
    /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/,
    /^(blue|violet|orange|gray)$/,
];

const RECEIVED_EMAIL_ATTRIBUTES: Record<string, sanitizeHtml.AllowedAttribute[]> = {
    a: ["href", "target"],
    img: ["src", "alt", "width", "height"],
    p: ["style"],
    span: ["style"],
    div: ["style"],
    blockquote: ["style"],
    td: ["style", "colspan", "rowspan", "width", "height", "align", "valign"],
    th: ["style", "colspan", "rowspan", "width", "height", "align", "valign"],
    table: ["style", "width", "cellpadding", "cellspacing", "border"],
};

const RECEIVED_EMAIL_STYLES: Record<string, Record<string, RegExp[]>> = {
    "*": {
        color: RECEIVED_EMAIL_COLOR_VALUES,
        "background-color": RECEIVED_EMAIL_COLOR_VALUES,
        "text-align": [/^left$/, /^right$/, /^center$/],
        "font-size": [/^\d+(?:px|em|%)$/],
    },
    p: {
        "font-size": [/^\d+rem$/],
        ...PARAGRAPH_SPACING_STYLES,
    },
};

const RECEIVED_EMAIL_OPTIONS: sanitizeHtml.IOptions = {
    allowedTags: [
        "br",
        "u",
        "b",
        "i",
        "em",
        "strong",
        "a",
        "img",
        "p",
        "span",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
        "ul",
        "ol",
        "li",
        "div",
        "table",
        "thead",
        "tbody",
        "tfoot",
        "tr",
        "td",
        "th",
        "blockquote",
        "hr",
        "pre",
        "code",
    ],
    allowedAttributes: RECEIVED_EMAIL_ATTRIBUTES,
    allowedSchemesByTag: {
        img: ["https"],
    },
    allowedStyles: RECEIVED_EMAIL_STYLES,
};

const NEW_TAB_LINK = sanitizeHtml.simpleTransform("a", {
    target: "_blank",
    rel: "noopener noreferrer",
});

const bodyMarkupOf = (htmlBody: string): string =>
    new DOMParser().parseFromString(htmlBody, "text/html").body.innerHTML;

export const getSanitizedMarkUpFromReceivedEmailBody = (
    htmlBody: string,
): SanitizedMarkUp => ({
    __html: sanitizeHtml(bodyMarkupOf(htmlBody), RECEIVED_EMAIL_OPTIONS),
});

export const getSanitizedMarkUpFromInboundLeadEmail = (
    htmlBody: string,
): SanitizedMarkUp => ({
    __html: sanitizeHtml(bodyMarkupOf(htmlBody), {
        ...RECEIVED_EMAIL_OPTIONS,
        allowedAttributes: {
            ...RECEIVED_EMAIL_ATTRIBUTES,
            a: ["href", "target", "rel"],
        },
        allowedStyles: {
            ...RECEIVED_EMAIL_STYLES,
            "*": { ...RECEIVED_EMAIL_STYLES["*"], "white-space": [/^pre-wrap$/] },
        },
        transformTags: { a: NEW_TAB_LINK },
    }),
});
